import React, { useState, useEffect } from 'react';
import './MonthlyCalendar.css';
import api from '../../api';

function MonthlyCalendar({ tasks, onTaskClick, onItemDrop, onAssignResource, companies }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [contextMenu, setContextMenu] = useState(null);
  const [resources, setResources] = useState([]);

  // Fetch users/resources for context menu
  useEffect(() => {
    async function fetchResources() {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/users');
        setResources(response.data);
      } catch (err) {
        console.error('Error fetching resources:', err);
      }
    }
    fetchResources();
  }, []);

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Start from the Sunday before the first day of the month
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    // End on the Saturday after the last day of the month
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  // Get tasks for a specific day
  const getTasksForDay = (date) => {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    return tasks.filter(task => {
      const taskStart = new Date(task.start || task.startDate);
      const taskEnd = new Date(task.end || task.endDate);
      
      // Task overlaps with this day
      return taskStart <= dayEnd && taskEnd >= dayStart;
    });
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Navigation
  const goToPreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle drop
  const handleDrop = (e, date) => {
    e.preventDefault();
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data && onItemDrop) {
        onItemDrop(data, date.toISOString().split('T')[0]);
      }
    } catch (err) {
      console.error('Error handling drop:', err);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Handle right-click
  const handleContextMenu = (e, task) => {
    e.preventDefault();
    
    // Calculate position to keep menu within viewport
    const menuWidth = 280; // Estimated menu width
    const menuHeight = 350; // Estimated menu height
    const padding = 10; // Padding from viewport edges
    
    let x = e.clientX;
    let y = e.clientY;
    
    // Adjust if menu would go off right edge
    if (x + menuWidth > window.innerWidth - padding) {
      x = window.innerWidth - menuWidth - padding;
    }
    
    // Adjust if menu would go off bottom edge
    if (y + menuHeight > window.innerHeight - padding) {
      y = window.innerHeight - menuHeight - padding;
    }
    
    // Ensure menu doesn't go off left or top edge
    if (x < padding) x = padding;
    if (y < padding) y = padding;
    
    setContextMenu({
      x,
      y,
      task,
    });
  };

  // Close context menu
  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', closeMenu);
      return () => document.removeEventListener('click', closeMenu);
    }
  }, [contextMenu]);

  // Get task color
  const getTaskColor = (task) => {
    // If task has a specific color assigned (e.g., company items), use it
    if (task.color) return task.color;
    
    const itemType = task.itemType || task.type;
    
    if (itemType === 'company') return task.color || '#3b82f6'; // Use assigned color or blue
    if (itemType === 'Meeting') return '#ec4899'; // Pink
    if (itemType === 'Milestone') return '#f97316'; // Orange
    if (itemType === 'CompanyTask' || itemType === 'task') return '#3b82f6'; // Blue
    return '#8b5cf6'; // Purple default
  };

  return (
    <div className="monthly-calendar">
      {/* Header */}
      <div className="calendar-header">
        <div className="calendar-nav">
          <button onClick={goToPreviousMonth} className="nav-btn">
            ◀
          </button>
          <button onClick={goToToday} className="today-btn">
            Today
          </button>
          <button onClick={goToNextMonth} className="nav-btn">
            ▶
          </button>
          <h2 className="calendar-title">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {/* Day Headers */}
        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
          <div key={day} className="calendar-day-header">
            {day}
          </div>
        ))}

        {/* Calendar Days */}
        {calendarDays.map((date, index) => {
          const dayTasks = getTasksForDay(date);
          const today = isToday(date);
          const currentMonth = isCurrentMonth(date);

          return (
            <div
              key={index}
              className={`calendar-day ${!currentMonth ? 'other-month' : ''} ${today ? 'today' : ''}`}
              onDrop={(e) => handleDrop(e, date)}
              onDragOver={handleDragOver}
            >
              <div className="day-number">
                {date.getDate() === 1 
                  ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : date.getDate()}
              </div>
              
              <div className="day-events">
                {dayTasks.slice(0, 3).map((task, idx) => (
                  <div
                    key={idx}
                    className="calendar-event"
                    style={{ backgroundColor: getTaskColor(task) }}
                    onClick={() => onTaskClick(task)}
                    onContextMenu={(e) => handleContextMenu(e, task)}
                    title={task.name || task.title}
                  >
                    <span className="event-time">
                      {task.startTime || '08:00'}
                    </span>
                    <span className="event-title">
                      {task.name || task.title}
                    </span>
                    {task.assignedUser && (
                      <span className="event-user">👤</span>
                    )}
                  </div>
                ))}
                
                {dayTasks.length > 3 && (
                  <div className="more-events">
                    +{dayTasks.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#3b82f6' }}></span>
          <span>Services</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#8b5cf6' }}></span>
          <span>Branch Setup</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#ec4899' }}></span>
          <span>Meetings</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#f97316' }}></span>
          <span>Milestones</span>
        </div>
      </div>

      {/* Context Menu - Multi-select */}
      {contextMenu && (
        <div 
          className="calendar-context-menu"
          style={{ 
            position: 'fixed', 
            top: contextMenu.y, 
            left: contextMenu.x,
            zIndex: 1000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header">
            Assign Resources (Multi-select):<br />
            <strong>{contextMenu.task.name || contextMenu.task.title}</strong>
          </div>
          <div className="context-menu-items">
            {resources.length === 0 ? (
              <div className="context-menu-item disabled">No resources available</div>
            ) : (
              resources.map((user) => {
                const assignedIds = contextMenu.task.assignedUserIds || 
                  (contextMenu.task.assignedUserId ? [contextMenu.task.assignedUserId] : []);
                const isAssigned = assignedIds.includes(user.id);
                return (
                  <div
                    key={user.id}
                    className={`context-menu-item ${isAssigned ? 'selected' : ''}`}
                    onClick={async () => {
                      if (onAssignResource) {
                        await onAssignResource(contextMenu.task, user, isAssigned);
                        // Optimistic update
                        setContextMenu(prev => prev ? {
                          ...prev,
                          task: {
                            ...prev.task,
                            assignedUserIds: isAssigned 
                              ? (prev.task.assignedUserIds || []).filter(id => id !== user.id)
                              : [...(prev.task.assignedUserIds || []), user.id]
                          }
                        } : null);
                      } else {
                        console.log('Assigning', user.name, 'to', contextMenu.task.name);
                      }
                      // Don't close menu - allow multi-select
                    }}
                  >
                    <input 
                      type="checkbox" 
                      checked={isAssigned}
                      onChange={() => {}}
                      style={{ marginRight: '8px', pointerEvents: 'none' }}
                    />
                    <span>{user.name}</span>
                    {user.role && <span className="context-menu-role">({user.role})</span>}
                  </div>
                );
              })
            )}
          </div>
          <div className="context-menu-footer">
            <button 
              className="context-menu-done-btn"
              onClick={() => setContextMenu(null)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyCalendar;

